import React from 'react';
import QRCode from 'qrcode.react';
import Showdown from 'showdown';
import { formatStringToClass } from '../util/format';
import { locationValueToString } from '@touchsource/wayfinding_lib';

const converter = new Showdown.Converter();

const columnsNotToRender = ['row_key', 'row_hash', 'landmark_id', 'mall_store_url'];

// Standard layout configuration for wayfinding/details pages
const STANDARD_SECTIONS = [
	{ typeFilter: ["Name", "Title"], section: 0, dataType: "Name" },
	{ typeFilter: ["Address"], section: 1, dataType: "Address" },
	{ typeFilter: ["Building"], section: 2, dataType: "Building" },
	{ typeFilter: ["Floor"], section: 3, dataType: "Floor" },
	{ typeFilter: ["Location (apt/suite)"], section: 4, dataType: "Location" },
	{ typeFilter: ["City", "State", "Zip"], section: 5, dataType: "Region" },
	{ typeFilter: ["Primary Phone", "Phone"], section: 6, dataType: "Phone" },
	{ typeFilter: ["Text Small", "Text Medium", "Text Large"], section: 8, dataType: "Text" },
	{ typeFilter: ["Logo", "Image", "Photo", "QR Code"], section: 9, dataType: "Graphics" }
];

const DetailsSection = ({
	detailsData,
	css,
	detailsUUID = '',
	config = {},
	handlers = {},
	activeButton = ''
}) => {
	// Default configuration
	const {
		showHeaders = false,
		labelSeparator = ':',
		typeFilter = null,
		limitOneImage = false,
		sectionClassName = '',
		section = '',
		dataType = '',
		touchAction = 0,
		disableInteractiveButtons = false
	} = config;

	// Default handlers (no-op functions if not provided)
	const {
		onPdfClick = () => {},
		onVideoClick = () => {},
		onUrlClick = () => {},
		onButtonDown = () => {},
		onButtonUp = () => {},
		navEvents = () => {}
	} = handlers;

	// Validate input
	if (!detailsData || detailsData.length === 0) {
		return null;
	}

	const detailsArray = [];
	const interactiveButtons = [];
	let imageAlreadyRendered = false;

	// Helper to get class name for detail type
	const getClass = (value) => {
		const classMap = {
			'Location (apt/suite)': 'location',
			'Primary Phone': 'primaryPhone',
			'Text Small': 'textSmall',
			'Text Medium': 'textMedium',
			'Text Large': 'textLarge'
		};
		return classMap[value] || value.toLowerCase();
	};

	// Helper to parse JSON phone data
	const parsePhoneData = (value) => {
		try {
			const jsonData = JSON.parse(value);
			if (jsonData && jsonData.cc && jsonData.area && jsonData.n) {
				return `${jsonData.area}-${jsonData.n}`;
			}
		} catch (err) {
			console.error("Error processing phone data", err);
		}
		return value;
	};

	// Filter data by type if typeFilter is provided
	const filteredData = typeFilter 
		? detailsData.filter(obj => typeFilter.includes(obj.type))
		: detailsData;

	// Process each detail object
	filteredData.forEach((obj, i) => {
		// Skip columns that should not be rendered
		if (columnsNotToRender.includes(obj.label)) {
			return;
		}

		// Skip if no value (except for certain types)
		if (!obj.value && obj.type !== 'QR Code') {
			return;
		}

		let value = obj.value;
		const uniqueClass = formatStringToClass(`${obj.type} ${obj.label}`);
		const hiddenClass = obj.hiddenAI ? 'hiddenAI' : '';

		// Check if value is JSON — format arrays as comma-separated, parse phone objects
		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) {
				value = locationValueToString(value);
			} else {
				value = parsePhoneData(value);
			}
		} catch (e) {
			// Not JSON, continue with original value
		}

		// Skip FULLDATETIME fields
		if (obj.label === "FULLDATETIME" || obj.label === "FULLDATETIMEUNIX") {
			return;
		}

		// Render based on type
		switch (obj.type) {
			case 'URL':
				if (value !== '' && !disableInteractiveButtons) {
					interactiveButtons.push(
						<div
							key={`url_${i}`}
							className={`${css.childDetailsBtn} childDetailsBtn childURLBtn ${uniqueClass} ${activeButton === 'urlButton' ? css.activeButton : ''}`}
							data-ts-id={`listingsDetailsUrlBtn.${detailsUUID}`}
							data-ts-index={i}
							onMouseDown={() => {
								onButtonDown('urlButton');
								navEvents('btnDebouncer', () => onUrlClick(value));
							}}
							onMouseUp={onButtonUp}
							onMouseLeave={onButtonUp}
							onTouchStart={() => {
								onButtonDown('urlButton');
								navEvents('btnDebouncer', () => onUrlClick(value));
							}}
							onTouchEnd={onButtonUp}
							onPointerDown={() => {
								onButtonDown('urlButton');
								navEvents('btnDebouncer', () => onUrlClick(value));
							}}
							onPointerUp={onButtonUp}
							onPointerLeave={onButtonUp}
						>
							{obj.label}
						</div>
					);
				}
				break;

			case 'PDF':
				if (value !== '' && touchAction !== 12 && !disableInteractiveButtons) {
					interactiveButtons.push(
						<div
							key={`pdf_${i}`}
							className={`${css.childDetailsBtn} childDetailsBtn childPDFBtn ${uniqueClass} ${activeButton === 'pdfButton' ? css.activeButton : ''}`}
							data-ts-id={`listingsDetailsPdfBtn.${detailsUUID}`}
							data-ts-index={i}
							onMouseDown={() => {
								onButtonDown('pdfButton');
								navEvents('btnDebouncer', () => onPdfClick(value));
							}}
							onMouseUp={onButtonUp}
							onMouseLeave={onButtonUp}
							onTouchStart={() => {
								onButtonDown('pdfButton');
								navEvents('btnDebouncer', () => onPdfClick(value));
							}}
							onTouchEnd={onButtonUp}
							onPointerDown={() => {
								onButtonDown('pdfButton');
								navEvents('btnDebouncer', () => onPdfClick(value));
							}}
							onPointerUp={onButtonUp}
							onPointerLeave={onButtonUp}
						>
							{obj.label}
						</div>
					);
				}
				break;

			case 'Video':
				if (value !== '' && touchAction !== 12 && !disableInteractiveButtons) {
					interactiveButtons.push(
						<div
							key={`video_${i}`}
							className={`${css.childDetailsBtn} childDetailsBtn childVideoBtn ${uniqueClass} ${activeButton === 'videoButton' ? css.activeButton : ''}`}
							data-ts-id={`listingsDetailsVideoBtn.${detailsUUID}`}
							data-ts-index={i}
							onMouseDown={() => {
								onButtonDown('videoButton');
								navEvents('btnDebouncer', () => onVideoClick(value));
							}}
							onMouseUp={onButtonUp}
							onMouseLeave={onButtonUp}
							onTouchStart={() => {
								onButtonDown('videoButton');
								navEvents('btnDebouncer', () => onVideoClick(value));
							}}
							onTouchEnd={onButtonUp}
							onPointerDown={() => {
								onButtonDown('videoButton');
								navEvents('btnDebouncer', () => onVideoClick(value));
							}}
							onPointerUp={onButtonUp}
							onPointerLeave={onButtonUp}
						>
							{obj.label}
						</div>
					);
				}
				break;

			case 'QR Code':
				detailsArray.push(
					<div
						key={`qrcode_${i}`}
						className={`${css.detailsQrCodeCont} detailsQrCodeCont ${uniqueClass}`}
						data-ts-id={`listingsDetailsQrCodeCont.${detailsUUID}`}
						data-ts-index={i}
					>
						<QRCode
							bgColor={"#fff"}
							data-ts-id={`listingsDetailsQrCode.${detailsUUID}`}
							data-ts-index={i}
							fgColor={"#000"}
							level="L"
							style={{ width: '100%', height: '100%', border: `10px solid #fff` }}
							value={value}
						/>
						<div
							className={`${css.qrCodeLabel} qrCodeLabel`}
							data-ts-id={`listingsDetailsQrCodeLabel.${detailsUUID}`}
							data-ts-index={i}
						/>
					</div>
				);
				break;

			case 'Logo':
			case 'Image':
			case 'Photo':
				if (!limitOneImage || !imageAlreadyRendered) {
					detailsArray.push(
						<img
							key={`image_${i}`}
							src={value}
							className={`${css.detailsData} detailsData ${`imgDetailsData_${i}`} ${uniqueClass} ${css[getClass(obj.type)]} ${getClass(obj.type)}`}
							data-ts-id={`listingsDetails${obj.type}.${detailsUUID}`}
							data-ts-index={i}
							alt={obj.label}
						/>
					);
					if (limitOneImage) {
						imageAlreadyRendered = true;
					}
				}
				break;

			case 'Text Large':
				const markdown = converter.makeHtml(value);
				detailsArray.push(
					<React.Fragment key={`textlarge_${i}`}>
						{showHeaders && (
							<div
								className={`${css.detailsLabel} detailsLabel detailsLabel_${i} ${uniqueClass}Label ${getClass(obj.type)}Label ${hiddenClass}`}
								data-ts-id={`listingsdetailsLabel.${detailsUUID}`}
								data-ts-index={i}
							>
								{obj.label}{labelSeparator}
							</div>
						)}
						<div
							className={`${css.editorOutput} editorOutput listingDetailsTextLarge ${uniqueClass} ${hiddenClass}`}
							dangerouslySetInnerHTML={{ __html: markdown }}
							data-ts-id={`listingsDetailsTextLarge.${detailsUUID}`}
							data-ts-index={i}
							style={{
								pointerEvents: 'none',
							}}
						/>
					</React.Fragment>
				);
				break;

			default:
				// Handle all other text-based types
				if (value) {
					detailsArray.push(
						<React.Fragment key={`default_${i}`}>
							{showHeaders ? (
								<>
									<div
										className={`${css.detailsLabel} detailsLabel detailsLabel_${i} ${uniqueClass}Label ${getClass(obj.type)}Label ${hiddenClass}`}
										data-ts-id={`listingsdetailsLabel.${detailsUUID}`}
										data-ts-index={i}
									>
										{obj.label}{labelSeparator}
									</div>
									<div
										className={`${css.detailsData} detailsData detailsData_${i} labelData_${i} labelValue ${uniqueClass} ${css[getClass(obj.type)]} ${getClass(obj.type)} ${hiddenClass}`}
										data-ts-id={`listingsDetailsData.${detailsUUID}`}
										data-ts-index={i}
									>
										{value}
									</div>
								</>
							) : (
								<div
									className={`${css.detailsData} detailsData detailsData_${i} ${uniqueClass} ${css[getClass(obj.type)]} ${getClass(obj.type)} ${hiddenClass}`}
									data-ts-id={`listingsDetailsData.${detailsUUID}`}
									data-ts-index={i}
								>
									{value}
								</div>
							)}
						</React.Fragment>
					);
				}
				break;
		}
	});

	// If we have interactive buttons, return them without wrapper
	if (interactiveButtons.length > 0) {
		return <>{interactiveButtons}</>;
	}

	// Return null if no content to render
	if (detailsArray.length === 0) {
		return null;
	}

	// Determine wrapper class name
	const wrapperClassName = [
		css.detailsSection,
		sectionClassName,
		section ? css[`detailsSection_${section}`] : '',
		section ? `detailsSection_${section}` : '',
		dataType ? css[`detailsData${dataType}`] : '',
		showHeaders ? `${css.labelWrapper} labelWrapper` : ''
	].filter(Boolean).join(' ');

	// Return wrapped content
	return (
		<div
			className={wrapperClassName}
			data-ts-id={`listingsDetailsSection.${detailsUUID}`}
			data-ts-index={section}
		>
			{detailsArray}
		</div>
	);
};

// Helper function to render multiple sections with standard layout
export const DetailsSections = ({
	detailsData,
	css,
	detailsUUID,
	config = {},
	handlers = {},
	activeButton = '',
	wrapperClassName = ''
}) => {
	const {
		showHeaders = true,
		labelSeparator = ':',
		disableInteractiveButtons = false
	} = config;

	const sections = STANDARD_SECTIONS.map((sectionConfig, index) => (
		<DetailsSection
			key={`section_${index}`}
			detailsData={detailsData}
			css={css}
			detailsUUID={detailsUUID}
			config={{
				showHeaders,
				labelSeparator,
				typeFilter: sectionConfig.typeFilter,
				section: sectionConfig.section,
				dataType: sectionConfig.dataType,
				disableInteractiveButtons
			}}
			handlers={handlers}
			activeButton={activeButton}
		/>
	));

	if (wrapperClassName) {
		return <div className={wrapperClassName}>{sections}</div>;
	}

	return <>{sections}</>;
};

export default DetailsSection;
